import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useHandOffToOE } from '@frame/components/oe/handOffToOE'
import { InternalEventType } from '@frame/lib/api/events/types'
import { buildEmployerHistoryViewProps } from '@frame/lib/api/events/utils'
import { DirectTask, DirectTaskState, TrueworkJsStrategy } from '@frame/lib/api/tasks/types'
import { useRootContext } from '@frame/lib/context'
import { useEventHandler } from '@frame/lib/hooks/useEventHandler'
import { useStrategies } from '@frame/lib/hooks/useStrategies'
import { useVerifyViaDocuments } from '@frame/lib/hooks/useVerifyViaDocuments'
import { useStore } from '@frame/lib/stores/useStore'
import { routes } from '@frame/routes'

export function useVerifyTask() {
  const { actions } = useRootContext()
  const { handleEvent } = useEventHandler()
  const navigate = useNavigate()
  const handOffToOE = useHandOffToOE()
  const verifyViaDocuments = useVerifyViaDocuments()
  const { bilEnabledStrategies, bilEnabledStrategiesNoBank, credentialsEnabled, documentsEnabled } = useStrategies()
  const { consumerInitiated, skipApplicantStatedInfo } = useStore(({ session }) => ({
    consumerInitiated: session?.consumer_initiated,
    skipApplicantStatedInfo: session?.configuration.skip_applicant_stated_info ?? false,
  }))
  const [loadingTaskId, setLoadingTaskId] = useState<string | null>(null)

  async function handleConnectBank(task: DirectTask) {
    navigate(`${routes.bankStatedInformation}/${task.id}`)
  }

  async function handleVerify(task: DirectTask, index?: number) {
    setLoadingTaskId(task.id)

    // Hand off to Direct Orchestration Engine sub-flow
    if (task.oe_flow_params) {
      handOffToOE(task.oe_flow_params)
      return
    }

    const shouldGoToStatedInfo = consumerInitiated && !skipApplicantStatedInfo
    const shouldGoToCompanySearch = bilEnabledStrategiesNoBank.length === 1 && credentialsEnabled
    const shouldGoToDocumentUpload =
      (bilEnabledStrategiesNoBank.length === 1 && documentsEnabled) || task.strategy === TrueworkJsStrategy.DOCUMENTS
    const shouldGoToSelectVerificationMethod = bilEnabledStrategiesNoBank.length > 1

    const params = new URLSearchParams({ q: task.employer_name ?? '' })

    if (task.state === DirectTaskState.SKIPPED) {
      await actions.unSkipDirectTask(task.id)
    }

    if (shouldGoToStatedInfo) {
      navigate(`${routes.statedInformation}/${task.id}?${params.toString()}`)
    } else if (shouldGoToCompanySearch) {
      navigate(`${routes.companySearch}/${task.id}?${params.toString()}`)
    } else if (shouldGoToDocumentUpload) {
      verifyViaDocuments(task)
    } else if (shouldGoToSelectVerificationMethod) {
      navigate(`${routes.selectVerificationMethod}/${task.id}?${params.toString()}`)
    } else {
      handleConnectBank(task)
    }

    handleEvent(
      InternalEventType.STARTED_EMPLOYER_VERIFICATION_FLOW,
      buildEmployerHistoryViewProps(false, bilEnabledStrategies, [task], index)
    )
  }

  return {
    loadingTaskId,
    setLoadingTaskId,
    handleVerify,
    handleConnectBank,
  }
}
